import { NavLink, Outlet, useLocation } from "react-router-dom";
import { ConfirmDialogHost } from "../shared/ConfirmDialogHost";
import { SaveFeedbackHost } from "../shared/SaveFeedbackHost";
import { IconBell, IconOrders } from "../shared/toolbarIcons";
import { UserProfileMenu } from "./UserProfileMenu";
import { NavIcon } from "./navIcons";

const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [{ to: "/", end: true, label: "Dashboard", icon: "dashboard" }]
  },
  {
    label: "Commerce",
    items: [
      { to: "customers-list", label: "Customers", icon: "customers" },
      { to: "vendors-list", label: "Vendors", icon: "vendors" },
      { to: "orders-list", label: "Orders", icon: "orders" },
      { to: "employees-list", label: "Employees", icon: "employees" }
    ]
  },
  {
    label: "Masters",
    items: [
      { to: "coupons-list", label: "Coupons", icon: "coupon" },
      { to: "countries-list", label: "Countries", icon: "globe" },
      { to: "states-list", label: "States", icon: "map" },
      { to: "cities-list", label: "Cities", icon: "building" },
      { to: "currencies-list", label: "Currencies", icon: "currency" },
      { to: "departments-list", label: "Departments", icon: "org" },
      { to: "designations-list", label: "Designations", icon: "org" }
    ]
  },
  {
    label: "Catalog",
    items: [
      { to: "categories-list", label: "Categories", icon: "category" },
      { to: "brands-list", label: "Brands", icon: "brand" },
      { to: "attributes-list", label: "Attributes", icon: "attribute" },
      { to: "products-list", label: "Products", icon: "product" }
    ]
  },
  {
    label: "Activities",
    items: [
      { to: "carts-list", label: "Carts", icon: "cart" },
      { to: "wishlists-list", label: "Wishlists", icon: "heart" },
      { to: "wallet-transactions-list", label: "Wallet Transactions", icon: "wallet" }
    ]
  }
];

function navItemActiveOverride(to, path) {
  const overrides = {
    "customers-list": ["/customers"],
    "vendors-list": ["/vendors"],
    "coupons-list": ["/coupons", "/coupon-usages"],
    "orders-list": ["/orders", "/order-details", "/order-details-list", "/orders-list"],
    "countries-list": ["/countries"],
    "states-list": ["/states"],
    "cities-list": ["/cities"],
    "currencies-list": ["/currencies"],
    "departments-list": ["/departments"],
    "designations-list": ["/designations"],
    "employees-list": ["/employees"],
    "wallet-transactions-list": ["/wallet-transactions", "/wallet-transactions-list"],
    "carts-list": ["/customer-carts"],
    "wishlists-list": ["/customer-wishlists"],
    "attributes-list": ["/attributes", "/attribute-values"],
    "products-list": ["/products"]
  };
  const paths = overrides[to];
  return paths ? paths.some((p) => path === p || path.startsWith(`${p}/`)) : false;
}

function NavItem({ item, pathname }) {
  const activeOverride = item.to === "/" ? false : navItemActiveOverride(item.to, pathname);

  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) => (isActive || activeOverride ? "nav-link active" : "nav-link")}
    >
      <NavIcon name={item.icon} />
      <span>{item.label}</span>
    </NavLink>
  );
}

export function AppLayout() {
  const location = useLocation();
  const isNotificationsActive =
    location.pathname === "/notifications-list" || location.pathname === "/notifications";
  const isOrdersActive =
    location.pathname === "/orders-list" ||
    location.pathname.startsWith("/orders") ||
    location.pathname.startsWith("/order-details");

  return (
    <div className="app-shell">
      <ConfirmDialogHost />
      <SaveFeedbackHost />
      <aside className="sidebar">
        <div className="sidebar-header">
          <img src={`${process.env.PUBLIC_URL}/images/mainlogo.png`} alt="OnlineX" className="sidebar-logo-img" />
          <div className="logo-text">
            <span className="company">OnlineX</span>
            <span className="product">Admin Console</span>
          </div>
        </div>
        <nav className="nav">
          {NAV_SECTIONS.map((section) => (
            <div key={section.label} className="nav-section">
              <div className="nav-section-heading">{section.label}</div>
              {section.items.map((item) => (
                <NavItem key={item.to} item={item} pathname={location.pathname} />
              ))}
            </div>
          ))}
        </nav>
      </aside>
      <div className="main">
        <header className="topbar">
          <div className="topbar-search">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input type="search" placeholder="Search customers, orders, products..." aria-label="Search admin" />
          </div>
          <div className="topbar-actions">
            <NavLink
              to="/orders-list"
              className={isOrdersActive ? "topbar-icon-link topbar-icon-link--active" : "topbar-icon-link"}
              title="View Orders"
              aria-label="View orders"
            >
              <IconOrders />
            </NavLink>
            <NavLink
              to="/notifications-list"
              className={isNotificationsActive ? "topbar-icon-link topbar-icon-link--active" : "topbar-icon-link"}
              title="Notifications"
              aria-label="Open notifications"
            >
              <IconBell />
            </NavLink>
            <UserProfileMenu />
          </div>
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
